import React, { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { ReviewStars } from '@components/ReviewStars/ReviewStars'
import { ReviewItem } from '@components/TownReviews/ReviewItem'
import { Box, Divider, Paragraph, Stack, Text } from '@ui'
import { useReviews } from 'hooks/useReviews'
import { env } from 'utils'

interface TownReviewsSectionProps {
    spaceId: string | undefined
    showSummary?: boolean
}

const REVIEWS_PER_PAGE = 20

interface ReviewSummaryProps {
    reviewCount: number
    summaryText: string | undefined
    overallRating: number
    ratingDistribution: Array<{ stars: number; percentage: number }>
}

const ReviewSummary = ({
    reviewCount,
    summaryText,
    overallRating,
    ratingDistribution,
}: ReviewSummaryProps) => {
    const formattedRating = overallRating.toFixed(1)

    return (
        <Stack gap="md">
            <Stack gap>
                <Stack horizontal gap="x4">
                    <Stack grow gap minWidth="200" maxWidth="400">
                        <Box horizontal gap="sm" alignItems="center">
                            <ReviewStars rating={overallRating} size={16} />
                            <Text strong>{formattedRating}</Text>
                            <Paragraph strong>Overall</Paragraph>
                        </Box>
                        <Stack gap="sm">
                            {ratingDistribution.map(({ stars, percentage }) => (
                                <Stack horizontal key={stars} gap="xs" alignItems="center">
                                    <Box justifyContent="center" minWidth="x6">
                                        <Paragraph size="sm" whiteSpace="nowrap">
                                            {stars} Star{stars === 1 ? '' : 's'}
                                        </Paragraph>
                                    </Box>
                                    <Box grow>
                                        <Box
                                            grow
                                            height="x1"
                                            background="level3"
                                            borderRadius="sm"
                                            position="relative"
                                            overflow="hidden"
                                            rounded="md"
                                        >
                                            <Box
                                                rounded="md"
                                                height="x1"
                                                background="purpleGradient"
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </Box>
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>
                    {!!summaryText && (
                        <Box grow gap>
                            <Text strong>Customers Say</Text>
                            <Paragraph color="gray2">
                                <em>{summaryText}</em>
                            </Paragraph>
                            <Paragraph color="gray2">
                                <em>Generated from the text of customer reviews</em>
                            </Paragraph>
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Stack>
    )
}

export const TownReviewsSection = ({ spaceId, showSummary }: TownReviewsSectionProps) => {
    const { reviews, averageRating } = useReviews(spaceId)
    const [visibleReviews, setVisibleReviews] = useState(REVIEWS_PER_PAGE)

    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0.5,
        triggerOnce: false,
    })

    useEffect(() => {
        if (inView && visibleReviews < reviews.length) {
            setVisibleReviews((prev) => Math.min(prev + REVIEWS_PER_PAGE, reviews.length))
        }
    }, [inView, visibleReviews, reviews.length])

    const isReviewsEnabled = env.VITE_REVIEWS_ENABLED
    const summaryText = undefined

    // Calculate rating distribution
    const ratingDistribution = React.useMemo(() => {
        const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
        reviews.forEach((review) => {
            const rating = Math.round(review.rating) as 1 | 2 | 3 | 4 | 5
            if (rating >= 1 && rating <= 5) {
                counts[rating]++
            }
        })

        return [5, 4, 3, 2, 1].map((stars) => ({
            stars,
            percentage:
                reviews.length > 0
                    ? Math.round((counts[stars as keyof typeof counts] * 100) / reviews.length)
                    : 0,
        }))
    }, [reviews])

    if (reviews.length === 0 || !isReviewsEnabled) {
        return null
    }

    return (
        <>
            <Divider />
            <Stack gap="md">
                <Text strong size="lg">
                    {reviews.length} {reviews.length === 1 ? 'Review' : 'Reviews'}
                </Text>

                {showSummary && (
                    <ReviewSummary
                        reviewCount={reviews.length}
                        overallRating={averageRating}
                        ratingDistribution={ratingDistribution}
                        summaryText={summaryText}
                    />
                )}

                <Stack gap="sm">
                    {reviews.slice(0, visibleReviews).map((review) => (
                        <Box key={review.id} paddingY="sm">
                            <ReviewItem review={review} />
                        </Box>
                    ))}
                    {visibleReviews < reviews.length && <Box ref={loadMoreRef} height="x4" />}
                </Stack>
            </Stack>
        </>
    )
}
